import { ReactElement, useState } from "react";
import { Provider as ThemeProvider } from "@remo-co/ui-core/src/components/Provider";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogActions } from "@remo-co/ui-core/src/components/DialogActions";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogContentText } from "@remo-co/ui-core/src/components/DialogContentText";
import { DialogTitle } from "@remo-co/ui-core/src/components/DialogTitle";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { RemoTheme } from "@remo-co/ui-core/theme";
import { useSelector } from "react-redux";
import { StyledEngineProvider } from "@remo-co/ui-core/src/components/StyledEngineProvider";
import { Close as CloseIcon } from "@remo-co/ui-core/src/icons/Close";
import Avatar from "modules/userAvatar/Avatar";
import { useI18n } from "i18n";
import { selectIsInBroadcast } from "modules/broadcast/redux/selectors";
import { updateGlobalUserPreference } from "modules/globalUserPreference";
import { IUser } from "modules/app/types";
import { SxProps } from "@remo-co/ui-core/src/types";
import classnames from "classnames";
import { useAppDispatch } from "store/hooks";
import { updateEventUserPreference } from "services/firebaseService/userPreferences.firebase";
import { DoNotShowAgain } from "../DoNotShowAgain";
import useDialogNotificationState from "../../hooks/useDialogNotificationState";
import { closeDialogNotification } from "../../redux/dialogNotificationSlice";
import { styles } from "./styles";

export interface IDialogParams {
  message?: string;
  content?: ReactElement;
  title?: string | ReactElement;
  onConfirm?: () => void;
  onOption?: () => void;
  onDismiss?: () => void;
  onFooterAction?: () => void;
  confirmText?: string; // default is 'Ok'
  optionText?: string; // default button doesn't show
  dismissText?: string; // default is 'Ignore'
  footerActionText?: string;
  confirmTestId?: string;
  optionTestId?: string;
  dismissTestId?: string;
  footerTestId?: string;
  confirmColor?: string;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  optionDisabled?: boolean;
  dismissDisabled?: boolean;
  image?: string;
  user?: IUser; // for avatar displaying
  hideDismissButton?: boolean;
  hideCloseButton?: boolean;
  data?: unknown; // any custom data to pass to callbacks
  type?: string; // kind of notification to track
  hidePageInBackground?: boolean; // If we need to hide the background page
  confirmBtnId?: string;
  optionBtnId?: string;
  dismissBtnId?: string;
  className?: string;
  contentClassName?: string;
  hasDoNotShowAgain?: boolean;
  isDoNotShowGlobal?: boolean;
  dnsKey?: string;
  /** @deprecated Use actionStyles prop instead */
  actionsClassName?: string;
  actionStyles?: SxProps;
  confirmBtnClassName?: string;
  dismissBtnClassName?: string;
  lightDialog?: boolean;
  confirmVariant?: string;
  headerStyles?: SxProps;
  closeIconStyles?: SxProps;
  confirmBtnStyles?: SxProps;
  dismissBtnStyles?: SxProps;
}

export const DialogNotificationContainer = () => {
  const { getCurrentParams } = useDialogNotificationState();
  const dispatch = useAppDispatch();
  const isInBroadcast = useSelector(selectIsInBroadcast);
  const params = getCurrentParams();
  const { t } = useI18n(["common", "event", "micCamCheck"]);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  const onDoNotShowChange = (checked: boolean) => {
    setDoNotShowAgain(checked);
  };

  if (!params) {
    return null;
  }

  const handleConfirm = () => {
    const { isDoNotShowGlobal, dnsKey } = params;

    if (doNotShowAgain) {
      if (isDoNotShowGlobal) {
        updateGlobalUserPreference({ [dnsKey]: doNotShowAgain });
      } else {
        updateEventUserPreference({ [dnsKey]: doNotShowAgain });
      }
    }

    if (params.onConfirm) {
      params.onConfirm(params.data);
    }
    dispatch(closeDialogNotification());
  };

  const handleOption = () => {
    if (params.onOption) {
      params.onOption(params.data);
    }
    dispatch(closeDialogNotification());
  };

  const handleDismiss = () => {
    if (params.onDismiss) {
      params.onDismiss(params.data);
    }
    dispatch(closeDialogNotification());
  };

  const handleFooterAction = () => {
    if (params.onFooterAction) {
      params.onFooterAction(params.data);
    }
    dispatch(closeDialogNotification());
  };

  const handleClose = () => {
    dispatch(closeDialogNotification());
  };

  const {
    message,
    confirmText,
    optionText,
    dismissText,
    user,
    hideDismissButton,
    title,
    content,
    confirmColor,
    footerActionText,
    hideCloseButton,
    confirmBtnId,
    optionBtnId,
    dismissBtnId,
    hasDoNotShowAgain,
    confirmDisabled,
    confirmLoading,
    confirmTestId,
    optionDisabled,
    optionTestId,
    dismissTestId,
    footerTestId,
    lightDialog = false,
    hidePageInBackground,
    className,
    contentClassName,
    actionsClassName,
    actionStyles,
    confirmBtnClassName,
    dismissBtnClassName,
    headerStyles,
    closeIconStyles,
    confirmBtnStyles,
    dismissBtnStyles,
  } = params;

  const darkTheme = !lightDialog && isInBroadcast;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={darkTheme ? RemoTheme.dark : RemoTheme.light}>
        <Dialog
          open
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          data-testid="remo-alert-dialog"
          slotProps={{
            backdrop: {
              sx: hidePageInBackground ? styles.backdrop : undefined,
            },
          }}
          className={classnames(className)}
          aria-live="polite"
        >
          {title && (
            <DialogTitle
              sx={{
                ...styles.headerWithTitle,
                ...headerStyles,
              }}
            >
              {typeof title === "string" ? (
                <Typography variant="h3">{t(title)}</Typography>
              ) : (
                <Box>{title}</Box>
              )}

              {!hideCloseButton && (
                <IconButton
                  aria-label={t("modal.window.aria.label")}
                  onClick={handleClose}
                  size="small"
                  data-testid="dialog-close-button"
                >
                  <CloseIcon sx={closeIconStyles} />
                </IconButton>
              )}
            </DialogTitle>
          )}

          <DialogContent
            sx={{
              ...styles.content,
              flex: 1,
              display: "flex",
              alignItems: "center",
              paddingRight: !hideCloseButton ? "36px" : undefined,
            }}
            className={contentClassName}
          >
            {user && (
              <Box sx={styles.avatar}>
                <Avatar user={user} data-testid="avatar-pic" />
              </Box>
            )}
            {message && (
              <DialogContentText tabIndex={0} sx={styles.text}>
                {message}
              </DialogContentText>
            )}
            {content && (
              <DialogContentText sx={styles.text}>{content}</DialogContentText>
            )}
          </DialogContent>
          {hasDoNotShowAgain && (
            <DoNotShowAgain
              doNotShowAgain={doNotShowAgain}
              onValueChange={onDoNotShowChange}
            />
          )}
          <DialogActions
            sx={{ ...styles.actions, ...actionStyles }}
            className={actionsClassName}
          >
            <Button
              onClick={handleConfirm}
              disabled={confirmDisabled}
              variant="secondary"
              loading={!darkTheme && confirmLoading}
              size="sm"
              autoFocus={Boolean(darkTheme)}
              id={confirmBtnId || ""}
              style={
                confirmColor
                  ? {
                      background: confirmColor,
                    }
                  : {}
              }
              className={confirmBtnClassName}
              sx={confirmBtnStyles}
              data-testid={darkTheme ? "dialog-confirm-btn" : confirmTestId}
              color="blue"
              theme={darkTheme ? "dark" : "light"}
            >
              {t(confirmText) || "Ok"}
            </Button>
            {!hideDismissButton &&
              (darkTheme ? (
                <Button
                  onClick={handleDismiss}
                  variant="tertiary"
                  size="sm"
                  id={dismissBtnId || ""}
                  className={dismissBtnClassName}
                  sx={dismissBtnStyles}
                  color="blue"
                  data-testid={dismissTestId}
                  theme={darkTheme ? "dark" : "light"}
                >
                  {t(dismissText) || t("ignore")}
                </Button>
              ) : (
                <Button
                  variant="tertiary"
                  color="dark"
                  onClick={handleDismiss}
                  size="sm"
                  data-testid={dismissTestId}
                  className={dismissBtnClassName}
                  sx={dismissBtnStyles}
                  theme="light"
                >
                  {t(dismissText) || t("ignore")}
                </Button>
              ))}
            {optionText && (
              <Button
                onClick={handleOption}
                disabled={optionDisabled}
                variant="secondary"
                size="sm"
                id={optionBtnId || ""}
                data-testid={optionTestId}
                color="dark"
                theme={darkTheme ? "dark" : "light"}
              >
                {t(optionText)}
              </Button>
            )}
          </DialogActions>
          {handleFooterAction && footerActionText && (
            <Box sx={styles.footer}>
              <Typography
                sx={styles.footerButton}
                onClick={handleFooterAction}
                data-testid={footerTestId}
              >
                {t(footerActionText)}
              </Typography>
            </Box>
          )}
        </Dialog>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
